/**
 * Meeting Analysis Types
 *
 * TypeScript enums and interfaces for post-meeting audio analysis.
 * Single multilingual embedding per record (gemini-embedding-2 native cross-lingual).
 *
 * Related Story: 2.2
 */
// =============================================================================
// Meeting Status
// =============================================================================
export const MeetingStatus = {
    SCHEDULED: 'scheduled', // Meeting scheduled from calendar, not yet started
    ACTIVE: 'active', // Meeting in progress
    COMPLETED: 'completed', // Meeting ended, not yet analyzed (or analysis failed)
    ANALYZING: 'analyzing', // Analysis in progress
    ANALYZED: 'analyzed', // Analysis complete
    FAILED: 'failed', // Meeting processing failed
};
// Display metadata for UI. Record<MeetingStatus, ...> so a new status is a
// compile error until it gets a label here.
export const MeetingStatusMetadata = {
    scheduled: { label: 'Scheduled' },
    active: { label: 'Active' },
    completed: { label: 'Completed' },
    analyzing: { label: 'Analyzing' },
    analyzed: { label: 'Analyzed' },
    failed: { label: 'Failed' },
};
/**
 * Why a meeting transitioned out of `active`. Set on every terminal status flip
 * so support/admin tooling can distinguish a normal end from a crash-recovered
 * upload from a server-side abandon sweep.
 */
export const MeetingEndedReason = {
    TRANSCRIPT_UPLOADED: 'transcript_uploaded', // Graceful end OR crash recovery: client posted /transcript-complete
    USER_STOPPED: 'user_stopped', // The rep explicitly stopped recording from the overlay
    AUTO_ABANDONED: 'auto_abandoned', // Heartbeat went stale; reconciler closed it
};
// =============================================================================
// Meeting Event Types (for KPI tracking)
// =============================================================================
export const MeetingEventType = {
    // Recording lifecycle
    RECORDING_STARTED: 'recording_started',
    RECORDING_STOPPED: 'recording_stopped',
    // Coaching events
    FLASHCARD_SHOWN: 'flashcard_shown',
    FLASHCARD_DISMISSED: 'flashcard_dismissed',
    ACTION_ITEM_CAPTURED: 'action_item_captured',
    // Analysis lifecycle
    ANALYSIS_STARTED: 'analysis_started',
    ANALYSIS_COMPLETED: 'analysis_completed',
    ANALYSIS_FAILED: 'analysis_failed',
    // Real-time events (from Story 2.4)
    TRANSCRIPT_TURN_COMPLETED: 'transcript_turn_completed',
    COMPETITOR_DETECTED: 'competitor_detected',
    GATE_DECISION: 'gate_decision',
    COACHING_REQUESTED: 'realtime_coaching_requested',
    COACHING_RESPONSE: 'realtime_coaching_response',
    // Chat events
    CHAT_USER_MESSAGE: 'chat_user_message',
    CHAT_RESPONSE: 'chat_response',
    // Error tracking
    ERROR: 'error',
};
// =============================================================================
// Speaker Side (sales vs client)
// =============================================================================
export const SpeakerSide = {
    SALES: 'sales',
    CLIENT: 'client',
    UNKNOWN: 'unknown',
};
// =============================================================================
// Emotion Types
// =============================================================================
export const Emotion = {
    HAPPY: 'happy',
    SAD: 'sad',
    ANGRY: 'angry',
    NEUTRAL: 'neutral',
    EXCITED: 'excited',
};
// =============================================================================
// Overall Tone Types
// =============================================================================
export const OverallTone = {
    PROFESSIONAL: 'professional',
    CASUAL: 'casual',
    TENSE: 'tense',
    COLLABORATIVE: 'collaborative',
    ENTHUSIASTIC: 'enthusiastic',
};
// =============================================================================
// Client Tone Types (per-suggestion utterance classification, client-only)
// =============================================================================
export const ClientTone = {
    HESITANT: 'hesitant',
    CONFIDENT: 'confident',
    SKEPTICAL: 'skeptical',
    EXCITED: 'excited',
    FRUSTRATED: 'frustrated',
    CURIOUS: 'curious',
    NEUTRAL: 'neutral',
    ANXIOUS: 'anxious',
    DECISIVE: 'decisive',
    DEFENSIVE: 'defensive',
};
export const CLIENT_TONE_VALUES = [
    'hesitant', 'confident', 'skeptical', 'excited', 'frustrated',
    'curious', 'neutral', 'anxious', 'decisive', 'defensive',
];
/** One-line descriptions used by the LLM prompt to classify consistently */
export const CLIENT_TONE_DESCRIPTIONS = {
    hesitant: 'expressing uncertainty, stalling, asking for time to think',
    confident: 'speaking with conviction, asserting positions clearly',
    skeptical: 'doubting claims, asking probing or comparative questions',
    excited: 'showing enthusiasm, energy, positive anticipation',
    frustrated: 'showing irritation, exasperation, blocked progress',
    curious: 'asking clarifying or exploratory questions, neutral interest',
    neutral: 'matter-of-fact, no strong emotional signal',
    anxious: 'worried, expressing concerns, risk-focused',
    decisive: 'ready to commit, asking about next steps or close',
    defensive: 'protecting a position, deflecting, justifying',
};
/**
 * Chip color tokens per ClientTone. Maps to existing CSS variables in
 * tailwind-config/shared-styles.css (no new hex codes invented). Tones with
 * shared semantic families share a color: confident+decisive (green),
 * hesitant+anxious (amber), frustrated+defensive (red).
 */
export const CLIENT_TONE_COLORS = {
    hesitant: { bg: 'rgba(245, 158, 11, 0.15)', text: 'var(--color-warning-light)', border: 'rgba(245, 158, 11, 0.3)' },
    confident: { bg: 'rgba(74, 222, 128, 0.15)', text: 'var(--color-green-400)', border: 'rgba(74, 222, 128, 0.3)' },
    skeptical: { bg: 'rgba(139, 92, 246, 0.15)', text: 'var(--color-prep-purple)', border: 'rgba(139, 92, 246, 0.3)' },
    excited: { bg: 'rgba(20, 184, 166, 0.15)', text: 'var(--color-teal-light)', border: 'rgba(20, 184, 166, 0.3)' },
    frustrated: { bg: 'rgba(239, 68, 68, 0.15)', text: 'var(--color-danger-light)', border: 'rgba(239, 68, 68, 0.3)' },
    curious: { bg: 'rgba(59, 130, 246, 0.15)', text: 'var(--color-blue-400)', border: 'rgba(59, 130, 246, 0.3)' },
    neutral: { bg: 'rgba(136, 136, 136, 0.15)', text: 'var(--color-text-secondary)', border: 'rgba(136, 136, 136, 0.3)' },
    anxious: { bg: 'rgba(245, 158, 11, 0.15)', text: 'var(--color-warning-light)', border: 'rgba(245, 158, 11, 0.3)' },
    decisive: { bg: 'rgba(74, 222, 128, 0.15)', text: 'var(--color-green-400)', border: 'rgba(74, 222, 128, 0.3)' },
    defensive: { bg: 'rgba(239, 68, 68, 0.15)', text: 'var(--color-danger-light)', border: 'rgba(239, 68, 68, 0.3)' },
};
// =============================================================================
// Deal Outcome Types
// =============================================================================
export const DealOutcome = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
    NEUTRAL: 'neutral',
};
// =============================================================================
// Analysis Status
// =============================================================================
export const AnalysisStatus = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed',
};
// =============================================================================
// Quality Tier (reuse from KB for consistency)
// =============================================================================
export const QualityTier = {
    AUTO_APPLY: 'auto_apply', // Quality >= 0.8
    ADMIN_REVIEW: 'admin_review', // Quality 0.5-0.79
    ARCHIVE_ONLY: 'archive_only', // Quality < 0.5
};
// =============================================================================
// Meeting Note Attachments
// =============================================================================
/**
 * Hard limits for the inline Gemini path used by Revont. These are deliberately
 * smaller than the Gemini Files API storage ceiling because Revont does not use
 * the Files API for analysis.
 */
export const MAX_GEMINI_ANALYSIS_FILE_SIZE_BYTES = 100 * 1024 * 1024;
export const MAX_GEMINI_ANALYSIS_PDF_SIZE_BYTES = 50 * 1024 * 1024;
export const MAX_GEMINI_FILES_PER_PROMPT = 10;
/** Maximum size for a single KB import accepted by the current upload path. */
export const MAX_KB_IMPORT_FILE_SIZE_BYTES = MAX_GEMINI_ANALYSIS_FILE_SIZE_BYTES;
/** Maximum size for a standalone KB image sent to the configured OCR provider. */
export const MAX_KB_IMAGE_SIZE_BYTES = MAX_KB_IMPORT_FILE_SIZE_BYTES;
export const DEFAULT_ATTACHMENT_LIMITS = {
    maxFileSizeBytes: MAX_GEMINI_ANALYSIS_FILE_SIZE_BYTES,
    maxPdfSizeBytes: MAX_GEMINI_ANALYSIS_PDF_SIZE_BYTES,
    maxImageSizeBytes: MAX_GEMINI_ANALYSIS_FILE_SIZE_BYTES,
    maxAttachmentsPerMeeting: MAX_GEMINI_FILES_PER_PROMPT,
    maxImagesPerMeeting: 20,
    allowedMimeTypes: [
        // Documents
        'application/pdf',
        'application/json',
        'application/msword', // DOC
        'application/rtf',
        'application/vnd.oasis.opendocument.text', // ODT
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
        'application/vnd.ms-excel', // XLS
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
        'application/zip',
        'text/csv',
        'text/css',
        'text/html',
        'text/javascript',
        'text/markdown',
        'text/plain',
        'text/tab-separated-values', // TSV
        'text/rtf',
        'text/xml',
        // Images
        'image/bmp',
        'image/heic',
        'image/heif',
        'image/gif',
        'image/jpeg',
        'image/png',
        'image/webp',
        // Audio
        'audio/aac',
        'audio/aiff',
        'audio/flac',
        'audio/mp3',
        'audio/ogg',
        'audio/wav',
        // Video
        'video/3gpp',
        'video/avi',
        'video/mpeg',
        'video/mp4',
        'video/mpg',
        'video/quicktime',
        'video/webm',
        'video/wmv',
        'video/x-flv',
    ],
};
/**
 * Canonical filename extensions for the attachment MIME types above. Consumers
 * must use this registry instead of maintaining local accept lists. A file may
 * still arrive with an empty or incorrect browser MIME type, so extension
 * fallback is part of the shared contract.
 */
export const ATTACHMENT_EXTENSIONS_BY_MIME_TYPE = {
    'application/pdf': ['.pdf'],
    'application/json': ['.json'],
    'application/msword': ['.doc'],
    'application/rtf': ['.rtf'],
    'application/vnd.oasis.opendocument.text': ['.odt'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/zip': ['.zip'],
    'text/csv': ['.csv'],
    'text/tab-separated-values': ['.tsv'],
    'text/css': ['.css'],
    'text/html': ['.html', '.htm'],
    'text/javascript': ['.js'],
    'text/markdown': ['.md', '.markdown'],
    'text/plain': ['.txt'],
    'text/rtf': ['.rtf'],
    'text/xml': ['.xml'],
    'image/bmp': ['.bmp'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'image/gif': ['.gif'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'audio/aac': ['.aac'],
    'audio/mp3': ['.mp3'],
    'audio/flac': ['.flac'],
    'audio/ogg': ['.ogg'],
    'audio/wav': ['.wav'],
    'audio/aiff': ['.aiff', '.aif'],
    'video/3gpp': ['.3gp'],
    'video/mp4': ['.mp4'],
    'video/mpeg': ['.mpeg', '.mpg'],
    'video/mpg': ['.mpg'],
    'video/quicktime': ['.mov'],
    'video/avi': ['.avi'],
    'video/webm': ['.webm'],
    'video/wmv': ['.wmv'],
    'video/x-flv': ['.flv'],
};
/**
 * Intersection of Revont's existing attachment registry and the file/image
 * input formats supported directly by the OpenAI Luna Responses path.
 *
 * This is intentionally separate from DEFAULT_ATTACHMENT_LIMITS and
 * KB_IMPORT_CAPABILITIES. Meeting/email storage and KB Import retain their
 * existing policies; Chat uses this direct-input policy because it does not
 * normalize generic attachments before sending them to Luna.
 */
export const LUNA_DIRECT_ATTACHMENT_MIME_TYPES = [
    'application/pdf',
    'application/json',
    'application/msword',
    'application/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'text/css',
    'text/html',
    'text/javascript',
    'text/markdown',
    'text/plain',
    'text/tab-separated-values',
    'text/rtf',
    'text/xml',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/webp',
];
/** OpenAI's direct input_file ceiling per file and across file inputs. */
export const MAX_LUNA_DIRECT_FILE_SIZE_BYTES = 50 * 1024 * 1024;
export const MAX_LUNA_DIRECT_FILE_TOTAL_BYTES = 50 * 1024 * 1024;
export function getLunaDirectAttachmentExtensions() {
    return getAttachmentExtensionsForMimeTypes(LUNA_DIRECT_ATTACHMENT_MIME_TYPES);
}
export function getLunaDirectAttachmentAccept() {
    return [
        ...LUNA_DIRECT_ATTACHMENT_MIME_TYPES,
        ...getLunaDirectAttachmentExtensions(),
    ].join(',');
}
/**
 * Canonical KB import capability policy. Email and meeting attachment support
 * remains the broader DEFAULT_ATTACHMENT_LIMITS registry above. This smaller
 * policy declares which KB path owns each accepted format.
 */
export const KB_IMPORT_CAPABILITIES = [
    'application/pdf',
    'application/msword',
    'application/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/rtf',
    'text/plain',
    'text/markdown',
    'text/html',
    'application/json',
    'text/csv',
    'text/tab-separated-values',
].map((mimeType) => ({
    mimeType,
    mode: 'deterministic',
    providerAssistance: 'none',
})).concat(['image/gif', 'image/jpeg', 'image/png', 'image/webp'].map((mimeType) => ({
    mimeType,
    mode: 'provider-assisted',
    providerAssistance: 'image-ocr',
})));
/** Document sources that the KB importer can normalize into an article. */
export const KB_IMPORT_MIME_TYPES = KB_IMPORT_CAPABILITIES.map(({ mimeType }) => mimeType);
export function getKbImportCapability(mimeType) {
    return KB_IMPORT_CAPABILITIES.find((capability) => capability.mimeType === mimeType) ?? null;
}
export function getSupportedAttachmentExtensions() {
    return Object.values(ATTACHMENT_EXTENSIONS_BY_MIME_TYPE).flat();
}
export function getAttachmentExtensionsForMimeTypes(mimeTypes) {
    return mimeTypes.flatMap((mimeType) => ATTACHMENT_EXTENSIONS_BY_MIME_TYPE[mimeType] ?? []);
}
export function getAttachmentMimeTypeFromFilename(filename) {
    const extension = filename.slice(filename.lastIndexOf('.')).toLowerCase();
    for (const [mimeType, extensions] of Object.entries(ATTACHMENT_EXTENSIONS_BY_MIME_TYPE)) {
        if (extensions.includes(extension))
            return mimeType;
    }
    return null;
}
/**
 * Declarative analysis normalization for every supported source format. The
 * actual converters stay in the backend because they require Node-only
 * dependencies such as XLSX, Papa Parse, and LibreOffice.
 */
export const ATTACHMENT_ANALYSIS_TRANSFORMS_BY_MIME_TYPE = {
    'application/vnd.ms-excel': 'excel-to-csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel-to-csv',
    'application/msword': 'office-to-pdf',
    'application/vnd.oasis.opendocument.text': 'office-to-pdf',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'office-to-pdf',
};
export function getAttachmentAnalysisTransform(fileType, filename = '') {
    const mimeType = resolveAttachmentMimeType(fileType, filename);
    if (!isSupportedAttachment(mimeType, filename))
        return null;
    return ATTACHMENT_ANALYSIS_TRANSFORMS_BY_MIME_TYPE[mimeType] ?? 'direct';
}
export function resolveAttachmentMimeType(fileType, filename = '') {
    const mimeType = normalizeAttachmentMimeType(fileType);
    return DEFAULT_ATTACHMENT_LIMITS.allowedMimeTypes.includes(mimeType)
        ? mimeType
        : getAttachmentMimeTypeFromFilename(filename) ?? mimeType;
}
export function isSupportedAttachment(fileType, filename = '') {
    const mimeType = resolveAttachmentMimeType(fileType, filename);
    if (DEFAULT_ATTACHMENT_LIMITS.allowedMimeTypes.includes(mimeType))
        return true;
    return getAttachmentMimeTypeFromFilename(filename) !== null;
}
export function isLunaDirectAttachmentSupported(fileType, filename = '') {
    const mimeType = resolveAttachmentMimeType(fileType, filename);
    return LUNA_DIRECT_ATTACHMENT_MIME_TYPES.includes(mimeType);
}
export function getLunaDirectAttachmentAnalysisLimitBytes(fileType, filename = '') {
    const mimeType = resolveAttachmentMimeType(fileType, filename);
    if (mimeType.startsWith('image/'))
        return DEFAULT_ATTACHMENT_LIMITS.maxImageSizeBytes;
    return Math.min(getAttachmentAnalysisLimitBytes(mimeType, DEFAULT_ATTACHMENT_LIMITS, filename), MAX_LUNA_DIRECT_FILE_SIZE_BYTES);
}
export function getSupportedAttachmentAccept() {
    return [
        ...DEFAULT_ATTACHMENT_LIMITS.allowedMimeTypes,
        ...getSupportedAttachmentExtensions(),
    ].join(',');
}
/** Inline media payload ceiling when Revont does not use the Gemini Files API. */
export const MAX_INLINE_MEDIA_ANALYSIS_SIZE_BYTES = 20 * 1024 * 1024;
export function normalizeAttachmentMimeType(fileType) {
    const mimeType = fileType?.toLowerCase().split(';', 1)[0] ?? '';
    const aliases = {
        'audio/mpeg': 'audio/mp3',
        'audio/x-aiff': 'audio/aiff',
        'audio/x-wav': 'audio/wav',
        'video/x-msvideo': 'video/avi',
    };
    return aliases[mimeType] ?? mimeType;
}
export function getAttachmentAnalysisLimitBytes(fileType, limits = DEFAULT_ATTACHMENT_LIMITS, filename = '') {
    const mimeType = resolveAttachmentMimeType(fileType, filename);
    if (mimeType === 'application/pdf')
        return limits.maxPdfSizeBytes;
    if (mimeType.startsWith('audio/') || mimeType.startsWith('video/')) {
        return Math.min(limits.maxFileSizeBytes, MAX_INLINE_MEDIA_ANALYSIS_SIZE_BYTES);
    }
    return limits.maxFileSizeBytes;
}
/** Maximum individual file size Revont will store anywhere: 2GB. */
export const MAX_ATTACHMENT_STORAGE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;
/** Max general file size Revont sends for analysis: 100MB. */
export const MAX_MEETING_ATTACHMENT_SIZE = DEFAULT_ATTACHMENT_LIMITS.maxFileSizeBytes;
/** Max PDF size Revont sends for analysis: 50MB. */
export const MAX_PDF_ATTACHMENT_SIZE = DEFAULT_ATTACHMENT_LIMITS.maxPdfSizeBytes;
/** Max single image size: 100MB general Gemini ceiling. */
export const MAX_MEETING_NOTE_IMAGE_SIZE = DEFAULT_ATTACHMENT_LIMITS.maxImageSizeBytes;
/** Max 10 attachments per meeting (Gemini's per-prompt file limit). */
export const MAX_ATTACHMENTS_PER_MEETING = DEFAULT_ATTACHMENT_LIMITS.maxAttachmentsPerMeeting;
/** Max 20 images per meeting. */
export const MAX_IMAGES_PER_MEETING = DEFAULT_ATTACHMENT_LIMITS.maxImagesPerMeeting;
/** Allowed meeting attachment MIME types supported by the inline Gemini path. */
export const ALLOWED_MEETING_ATTACHMENT_TYPES = new Set(DEFAULT_ATTACHMENT_LIMITS.allowedMimeTypes);
export const MEETING_NOTE_IMAGE_MIME_TYPES = DEFAULT_ATTACHMENT_LIMITS.allowedMimeTypes.filter((mimeType) => mimeType.startsWith('image/'));
